import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ClipboardList, BookOpen, Award, FileText } from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import InstructorLessons from '../../components/academy/InstructorLessons';
import { useAuth } from '../../context/AuthContext';

/* Instructor dashboard — roster of students enrolled in courses they teach.
 * Backend: GET /api/academy/my/roster (instructor + admin only). */
export default function InstructorDashboard() {
  const { user, api } = useAuth();
  const [rows, setRows] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/academy/my/roster')
      .then(({ data }) => setRows(data.rows || []))
      .finally(() => setLoading(false));
    // Programs catalog feeds the lesson submission form.
    api.get('/api/academy/programs')
      .then(({ data }) => setPrograms(data.programs || data.rows || data || []))
      .catch(() => setPrograms([]));
  }, [api]);

  // Group enrollments by program
  const byProgram = useMemo(() => {
    const out = {};
    rows.forEach((r) => {
      out[r.program_slug] = out[r.program_slug] || { name: r.program_name, students: [] };
      out[r.program_slug].students.push(r);
    });
    return out;
  }, [rows]);

  const updateGrade = async (user_id, program_slug, grade, progress) => {
    try {
      await api.post(`/api/academy/admin/enrollments/${user_id}/${program_slug}/grade`, {
        grade, progress: progress != null ? Number(progress) : undefined,
      });
      // optimistic refetch
      const { data } = await api.get('/api/academy/my/roster');
      setRows(data.rows || []);
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar portalLabel="FACULTY ACCESS" />
      <main className="max-w-[1200px] mx-auto px-4 lg:px-6 py-8">
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">Welcome, {user?.name}</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Your roster, grouped by course.</p>

        {/* ---- Faculty quick-stats strip ---- */}
        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6" data-testid="faculty-stats">
            <FacultyStat icon={Users} label="Students" value={rows.length} />
            <FacultyStat icon={BookOpen} label="Active programs" value={Object.keys(byProgram).length} />
            <FacultyStat icon={Award} label="Certificate-eligible" value={rows.filter((r) => (r.progress || 0) >= 100).length} />
          </div>
        )}

        {/* ---- Faculty quick-links ---- */}
        <div className="rounded-2xl border border-[#E5E5E7] bg-white p-4 mb-6 flex flex-wrap items-center gap-3"
          data-testid="faculty-quick-links">
          <span className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">Quick links</span>
          <Link to="/academy/courses" data-testid="faculty-link-courses"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
            <BookOpen className="w-3 h-3 text-[#c9a227]" /> Course pages
          </Link>
          <Link to="/academy/documents" data-testid="faculty-link-docs"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
            <FileText className="w-3 h-3 text-[#c9a227]" /> Documents
          </Link>
          <Link to="/academy/support" data-testid="faculty-link-support"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold uppercase tracking-wider border border-[#E5E5E7] text-[#0e0e10] hover:border-[#c9a227]">
            <ClipboardList className="w-3 h-3 text-[#c9a227]" /> Tech support
          </Link>
        </div>

        {loading ? <div className="text-[#6E6E73]">Loading…</div> :
          rows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center">
              <Users className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
              <div className="text-[16px] font-bold text-[#1D1D1F]">No students assigned yet</div>
              <p className="text-[13.5px] text-[#6E6E73]">An admin needs to assign you to a course as instructor first.</p>
            </div>
          ) : (
            Object.entries(byProgram).map(([slug, prog]) => (
              <div key={slug} className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white mb-6">
                <div className="bg-[#0e0e10] text-white px-6 py-3.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-[#f5a623]" />
                    <span className="text-[14px] font-bold tracking-[0.18em] uppercase">{prog.name}</span>
                  </div>
                  <span className="text-[12px] text-white/60">{prog.students.length} students</span>
                </div>
                <table className="w-full text-[13px]">
                  <thead className="bg-[#FAFAFA] text-[#6E6E73]">
                    <tr>
                      <th className="px-4 py-2 text-left text-[10px] uppercase">Student</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase">Tier</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase">Schedule</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase">Progress</th>
                      <th className="px-4 py-2 text-left text-[10px] uppercase">Grade</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5E5E7]">
                    {prog.students.map((s) => (
                      <tr key={`${s.user_id}-${s.program_slug}`} data-testid={`roster-row-${s.user_id}`}>
                        <td className="px-4 py-2 font-bold text-[#1D1D1F]">{s.user?.name || s.user?.email || '?'}<br /><span className="text-[11px] font-normal text-[#6E6E73]">{s.user?.email}</span></td>
                        <td className="px-4 py-2 text-[#6E6E73]">{s.tier_id}</td>
                        <td className="px-4 py-2 text-[#6E6E73]">{s.schedule || 'Self-paced'}</td>
                        <td className="px-4 py-2">
                          <input type="number" min="0" max="100" defaultValue={s.progress || 0}
                            data-testid={`roster-progress-${s.user_id}`}
                            onBlur={(e) => updateGrade(s.user_id, s.program_slug, s.grade, e.target.value)}
                            className="w-20 px-2 py-1 rounded border border-[#E5E5E7] outline-none text-[13px] focus:border-[#c9a227]" />
                          <span className="text-[#6E6E73] text-[12px]">%</span>
                        </td>
                        <td className="px-4 py-2">
                          <input defaultValue={s.grade || ''} placeholder="—"
                            data-testid={`roster-grade-${s.user_id}`}
                            onBlur={(e) => updateGrade(s.user_id, s.program_slug, e.target.value, s.progress)}
                            className="w-20 px-2 py-1 rounded border border-[#E5E5E7] outline-none text-[13px] focus:border-[#c9a227]" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}

        <InstructorLessons programs={programs} />
      </main>
    </div>
  );
}

function FacultyStat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[#E5E5E7] bg-white p-4 flex items-center gap-3">
      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#fff8e7] text-[#a8871a]">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#a8871a]">{label}</div>
        <div className="text-[22px] font-extrabold text-[#0e0e10] tabular-nums leading-none mt-0.5">{value}</div>
      </div>
    </div>
  );
}
